import React, { memo, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { theme } from "uikit";

import { Language } from "../../assets/languages/langs";

import { LOGIN_LANGUAGE_ORDER, LOGIN_TEXTS } from "./loginTexts";

const Root = styled.div`
	position: fixed;
	top: 16px;
	right: 16px;
	z-index: 10;

	font-family: Lato;
	font-size: 13px;
	line-height: 16px;
`;

const Toggle = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;

	padding: 7px 10px;

	border: 1px solid transparent;
	border-radius: 5px;

	font: inherit;
	color: ${theme.colors.secondary};
	background: transparent;

	cursor: pointer;

	&:hover,
	&[aria-expanded="true"] {
		border-color: #e3e6e8;
		background: #ffffff;
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 1px;
	}
`;

const Caret = styled.span<{ open: boolean }>`
	width: 0;
	height: 0;

	border-left: 4px solid transparent;
	border-right: 4px solid transparent;
	border-top: 5px solid currentColor;

	transform: rotate(${({ open }) => (open ? "180deg" : "0deg")});
`;

const Menu = styled.ul`
	position: absolute;
	top: calc(100% + 4px);
	right: 0;

	min-width: 160px;
	margin: 0;
	padding: 4px 0;

	list-style: none;

	border: 1px solid #e3e6e8;
	border-radius: 5px;
	background: #ffffff;
	box-shadow: 0 2px 4px 0 rgba(34, 36, 38, 0.12),
		0 2px 10px 0 rgba(34, 36, 38, 0.15);
`;

const Item = styled.button<{ current: boolean }>`
	display: block;

	width: 100%;
	padding: 8px 14px;

	border: 0;

	font: inherit;
	font-weight: ${({ current }) => (current ? 600 : 400)};
	text-align: left;
	color: ${theme.colors.primary};
	background: ${({ current }) => (current ? "#f2f4f5" : "transparent")};

	cursor: pointer;

	&:hover,
	&:focus-visible {
		outline: none;
		background: #ebedef;
	}
`;

/** Language of the login screen, top right. Native names: anyone finds theirs. */
const LanguagePicker = memo(({ value, onChange }: LanguagePicker.Props) => {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return undefined;

		const onPointerDown = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node))
				setOpen(false);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};

		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open]);

	const texts = LOGIN_TEXTS[value] ?? LOGIN_TEXTS.en;

	return (
		<Root ref={rootRef}>
			<Toggle
				type="button"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={texts.language}
				onClick={() => setOpen((prev) => !prev)}
			>
				{texts.languageName}
				<Caret open={open} aria-hidden="true" />
			</Toggle>
			{open && (
				<Menu role="listbox" aria-label={texts.language}>
					{LOGIN_LANGUAGE_ORDER.map((lang) => (
						<li key={lang}>
							<Item
								type="button"
								role="option"
								aria-selected={lang === value}
								current={lang === value}
								lang={lang}
								onClick={() => {
									onChange(lang);
									setOpen(false);
								}}
							>
								{LOGIN_TEXTS[lang].languageName}
							</Item>
						</li>
					))}
				</Menu>
			)}
		</Root>
	);
});

declare namespace LanguagePicker {
	interface Props {
		value: Language;
		onChange: (lang: Language) => void;
	}
}

export default LanguagePicker;
